import { computeThemeScores, themeScoreLabel } from "../lib/scoring";
import type { BloomAnswers, BloomThemeScore } from "../types/bloom-content";

interface BloomShareResultProps {
  data: Record<string, unknown> | null;
}

function isBloomAnswers(value: unknown): value is BloomAnswers {
  if (!value || typeof value !== "object") return false;
  for (const v of Object.values(value)) {
    if (typeof v !== "number") return false;
  }
  return true;
}

function ThemeRow({ score }: { score: BloomThemeScore }) {
  const pct = Math.round((score.score / score.maxScore) * 100);
  return (
    <li className="bloom-share-result__row">
      <div className="bloom-share-result__row-head">
        <span className="bloom-share-result__label">{score.label}</span>
        <span className="bloom-share-result__score">
          {score.score}/{score.maxScore}
        </span>
      </div>
      <div
        className="bloom-share-result__bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={score.maxScore}
        aria-valuenow={score.score}
        aria-label={`${score.label}: ${score.score} van ${score.maxScore}`}
      >
        <span className="bloom-share-result__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="bloom-share-result__tier">{themeScoreLabel(score.score, score.maxScore)}</span>
    </li>
  );
}

export function BloomShareResult({ data }: BloomShareResultProps) {
  const answers = data?.answers;
  if (!isBloomAnswers(answers)) {
    return (
      <p className="bloom-share-result__empty">Geen gedetailleerde scores beschikbaar.</p>
    );
  }

  const scores = computeThemeScores(answers);
  const total = scores.reduce((sum, s) => sum + s.score, 0);
  const maxTotal = scores.reduce((sum, s) => sum + s.maxScore, 0);
  const totalPct = Math.round((total / maxTotal) * 100);

  return (
    <div className="bloom-share-result">
      <ol className="bloom-share-result__list">
        {scores.map((score) => (
          <ThemeRow key={score.themeId} score={score} />
        ))}
      </ol>
      <div className="bloom-share-result__total">
        <div className="bloom-share-result__row-head">
          <span className="bloom-share-result__label bloom-share-result__label--strong">
            Totaal
          </span>
          <span className="bloom-share-result__score bloom-share-result__score--strong">
            {total}/{maxTotal}
          </span>
        </div>
        <div
          className="bloom-share-result__bar bloom-share-result__bar--total"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={maxTotal}
          aria-valuenow={total}
          aria-label={`Totaal: ${total} van ${maxTotal}`}
        >
          <span className="bloom-share-result__fill" style={{ width: `${totalPct}%` }} />
        </div>
      </div>
    </div>
  );
}
